import React, { useState, useContext, useEffect } from 'react';
import { Form, Button, Col, Row, Modal, InputGroup } from 'react-bootstrap';
import { AuthContext } from '../context/AuthContext';
import { db } from '../FireBase';
import { updateDoc, getDocs, query, where, collection, writeBatch } from 'firebase/firestore';
import ToastNotification from './ToastAlert';
import Loading from '../components/Loading';
import { useNavigate } from 'react-router-dom';
import { Mail, Phone, User } from 'lucide-react';

const AccountInfo = () => {
  const navigate = useNavigate();
  const { currentUser, logout } = useContext(AuthContext);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
  });

  const [loading, setLoading] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [variant, setToastVariant] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Fetch user data
  useEffect(() => {
    const fetchUserData = async () => {
      setLoading(true);
      if (currentUser?.cusmid) {
        try {
          const q = query(collection(db, 'users'), where('cusmid', '==', currentUser.cusmid));
          const querySnapshot = await getDocs(q);

          if (!querySnapshot.empty) {
            const docSnap = querySnapshot.docs[0];
            const userData = docSnap.data();
            setFormData({
              name: userData.name || '',
              email: userData.email || '',
              phone: userData.phone || '',
            });
          }
        } catch (error) {
          console.error('Error fetching user data:', error);
        } finally {
          setLoading(false);
        }
      } else {
        setLoading(false);
      }
    };

    fetchUserData();
  }, [currentUser]);

  // Update user info
  const handleSubmit = async (e) => {
    e.preventDefault();
    const { name, email, phone } = formData;

    if (!name.trim() || !phone.trim() || !email.trim()) {
      setToastMessage('Name and phone,email are required.');
      setToastVariant('warning');
      setShowToast(true);
      return;
    }

    try {
      setLoading(true);
      const q = query(collection(db, 'users'), where('cusmid', '==', currentUser.cusmid));
      const querySnapshot = await getDocs(q);

      if (!querySnapshot.empty) {
        const docRef = querySnapshot.docs[0].ref;
        await updateDoc(docRef, { name, phone, email,profileComplete:true });

        setToastMessage('Account details updated successfully!');
        setToastVariant('success');
        setShowToast(true);
      } else {
        setToastMessage('User not found.');
        setToastVariant('danger');
        setShowToast(true);
      }
    } catch (err) {
      setToastMessage('Failed to update account details: ' + err.message);
      setToastVariant('danger');
      setShowToast(true);
    } finally {
      setLoading(false);
    }
  };

  // Delete account
  const handleDeleteAccount = async () => {
    try {
      setDeleting(true);
      const batch = writeBatch(db);

      const q = query(collection(db, 'users'), where('cusmid', '==', currentUser.cusmid));
      const querySnapshot = await getDocs(q);

      if (!querySnapshot.empty) {
        querySnapshot.forEach((docSnap) => {
          batch.delete(docSnap.ref);
        });

        await batch.commit();

        logout();
        localStorage.removeItem('userdetails');
        navigate('/account');

        setToastMessage('Account deleted successfully.');
        setToastVariant('success');
        setShowToast(true);
      } else {
        setToastMessage('User not found.');
        setToastVariant('danger');
        setShowToast(true);
      }
    } catch (error) {
      setToastMessage('Error deleting account: ' + error.message);
      setToastVariant('danger');
      setShowToast(true);
    } finally {
      setDeleting(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  if (loading) {
    return (
      <div className="loading-screen">
        <Loading />
      </div>
    );
  }

  return (
    <>
      <ToastNotification
        show={showToast}
        onClose={() => setShowToast(false)}
        message={toastMessage}
        variant={variant}
      />

      <Form onSubmit={handleSubmit}>
        <h4>Account Info</h4>
       
            <Form.Group controlId="formName" className="mb-3">
              <Form.Label>Name</Form.Label>
              <InputGroup>
              <InputGroup.Text><User size={16} /></InputGroup.Text>
              <Form.Control
                type="text"
                name="name"
                placeholder="Enter your name"
                value={formData.name}
                onChange={handleInputChange}
                required
               
              />
              </InputGroup>
            </Form.Group>

            <Form.Group controlId="formPhone" className="mb-3">
              <Form.Label>Phone</Form.Label>
              <InputGroup>
              <InputGroup.Text><Phone size={16} /></InputGroup.Text>
              <Form.Control
                type="tel"
                name="phone"
                placeholder="Enter your phone"
                value={formData.phone}
                onChange={handleInputChange}
                required
                
              />
              </InputGroup>
            </Form.Group>

            <Form.Group controlId="formEmail" className="mb-3">
              <Form.Label>Email</Form.Label>
            
       
            <InputGroup>
                    <InputGroup.Text><Mail size={16} /></InputGroup.Text>
                    <Form.Control
                     type="email"
                     name="email"
                     placeholder="Enter email"
                     value={formData.email}
                     onChange={handleInputChange}
                     required
                    
                    />
                  </InputGroup>
                </Form.Group>

            <Button className='btn-own' type="submit" disabled={loading} style={{ width: '100%' }}>
              {loading ? 'Updating...' : 'Update Details'}
            </Button>
         
      </Form>

      <Button

        onClick={() => setShowDeleteModal(true)}
        disabled={loading || deleting}
        style={{ marginTop: '15px' }}
        className='btn-own-danger' 
      >
        Delete Account
      </Button>

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Account Deletion</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete your account? This action cannot be undone.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)} disabled={deleting}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDeleteAccount} disabled={deleting}>
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default AccountInfo;
